import { QuoteSide } from "@/lib/interfaces and Enums/ENUMQuoteSide";
import { getBestCounterparty } from "../actionSummaryMainTableColDefs";
import { StrategyType } from "@/lib/interfaces and Enums/ENUMStrategyType";
import { ExecutionStrategyStatus } from "@/lib/interfaces and Enums/ENUMExecutionStrategyStatus";
import {
  getQuoteTypeFromBlockId,
  statusFromAwarded,
} from "../colDefs/colDefsShared";
import { QuoteType } from "@/lib/interfaces and Enums/ENUMQuoteType";
import { findCPOnTrade } from "../tradeLookupHelpers";

export interface IQuoteShadeParams {
  params: any;
  theme: string | undefined;
  liveStatus: ExecutionStrategyStatus;
  liveQuoteSide: QuoteSide;
  isLeg: boolean;
  isER: boolean;
  isES: boolean;
  isOp: boolean;
  fullTradeData: any;
  // Live trade from tradeDataStore subscription. Preferred source for quoteType
  // so the cell reflects edits applied to the store (e.g. after Edit Panel PUT)
  // without needing the row to be collapsed and re-expanded.
  thisTrade?: any;
}

export function quoteShade(quoteShadeParams: IQuoteShadeParams) {
  const {
    params,
    theme,
    liveStatus,
    liveQuoteSide,
    isLeg,
    isER,
    isES,
    isOp,
    fullTradeData,
    thisTrade,
  } = quoteShadeParams;

  const data = params.data;
  const tableLevel = params.tableLevel;
  let quoteType;
  let quoteSide;
  let isOutright = false;
  if (fullTradeData.strategyType == StrategyType.Outright) isOutright = false;

  let field;
  if (params.colDef.field == "bid") {
    field = QuoteSide.Bid;
  } else if (params.colDef.field == "ask") {
    field = QuoteSide.Ask;
  } else {
    field = QuoteSide.Mid;
  }

  if (field == QuoteSide.Mid) return "";

  if (isES) {
    const bestCounterparty = getBestCounterparty(thisTrade ?? params.data);

    if (bestCounterparty?.legs?.length > 0) {
      quoteType = bestCounterparty.quoteType;
      quoteSide = isOutright
        ? bestCounterparty.legs[0].quoteSide
        : bestCounterparty.quoteSideER;
    }
  } else if (isER) {
    if (isOp) {
      const liveER = findCPOnTrade(thisTrade, data.executionRequestId);
      quoteType = liveER?.quoteType ?? data.quoteType;
      quoteSide = isOutright ? data.legs?.[0]?.quoteSide : data.quoteSideER;
    } else {
      // RP: the ER row IS the trade (returnRowDataAtDetailTable passes [params.data]),
      // so quoteType / quoteSide live directly on thisTrade. AG Grid never re-fetches
      // the detail row data, so reading quoteSide from `data` would be stale after the
      // OP toggles Two-Way ↔ One-Way and sends — falling back to `data` only when the
      // store has no live trade yet.
      quoteType = thisTrade?.quoteType ?? data.quoteType;
      quoteSide = isOutright
        ? thisTrade?.legs?.[0]?.quoteSide ?? data.legs?.[0]?.quoteSide
        : thisTrade?.quoteSideER ?? data.quoteSideER;
    }
  } else if (tableLevel == "Leg") {
    const liveTrade = thisTrade ?? fullTradeData;
    quoteType =
      fullTradeData.role == "OP"
        ? getQuoteTypeFromBlockId(liveTrade, params.data.blockId)
        : liveTrade.quoteType;
    if (fullTradeData.role == "OP") {
      quoteSide = data.quoteSide;
    } else {
      // RP: detail row data is stale after OP-side Two-Way ↔ One-Way send, so look
      // up the live leg in the store by blockId.
      const liveLeg = thisTrade?.legs?.find(
        (l: any) => l.blockId === params.data.blockId,
      );
      quoteSide = liveLeg?.quoteSide ?? data.quoteSide;
    }
  }

  function returnInputStyling(theme: string | undefined) {
    const safeTheme = theme ?? "light";
    return safeTheme == "light" ? "quoteSideLight" : "quoteSideDark";
  }
  function noInputStyling(theme: string | undefined) {
    const safeTheme = theme ?? "light";

    return safeTheme == "light"
      ? "inputCellNotRequiredLight"
      : "inputCellNotRequiredDark";
  }

  //Logic to determine what styling to return
  if (quoteType == QuoteType.OneWay) {
    return quoteSide == field
      ? returnInputStyling(theme)
      : noInputStyling(theme);
  } else if (quoteType == QuoteType.TwoWay) {
    //if status is before Awarded, do not show direction. Both bid and ask have input styling
    if (!statusFromAwarded.includes(liveStatus)) {
      return returnInputStyling(theme);
    }

    if (
      statusFromAwarded.includes(liveStatus) &&
      ((isLeg && liveQuoteSide == field) ||
        (isOp && !isLeg && quoteSide == field) ||
        (!isOp && !isLeg && liveQuoteSide == field))
    ) {
      return returnInputStyling(theme);
    } else {
      return noInputStyling(theme);
    }
  } else if (isES
    && (liveStatus == ExecutionStrategyStatus.New || liveStatus == ExecutionStrategyStatus.ReCreated || liveStatus == ExecutionStrategyStatus.NotDealt || liveStatus == ExecutionStrategyStatus.Cancelled)
    && data.quoteSideES && data.quoteSideES == field
  ) {
    return returnInputStyling(theme);
  } else {
    return noInputStyling(theme);
  }
}
